import React, { useState, useEffect, useMemo } from "react";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { motion } from "framer-motion";
import { Users, Car, DollarSign, Star } from "lucide-react";
import { AnimatePresence } from "framer-motion";
import {
  Sun,
  Moon,
  RefreshCw,
  Download,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { API_URL } from "../../../components/config";
const ITEMS_PER_PAGE = 6;
const AUTO_SCROLL_INTERVAL = 3000;
const COLORS = [
  "#8884d8",
  "#82ca9d",
  "#ffc658",
  "#ff8042",
  "#0088FE",
  "#00C49F",
];

const StsDashboard = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [data, setData] = useState([]); // State to store fetched data
  const [loading, setLoading] = useState(true); // State to manage loading status
  const [error, setError] = useState(null); // State to manage errors
  const [statistics, setStatistics] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [autoScroll, setAutoScroll] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const handlePageChange = (direction) => {
    setAutoScroll(false);
    setCurrentPage((prev) => {
      if (direction === "next") return (prev + 1) % totalPages;
      return prev === 0 ? totalPages - 1 : prev - 1;
    });
  };

  // Keys to ignore in statistics
  const keysToIgnore = [
    "driver_open_balance",
    "driver_top_up",
    "driver_billed_charges",
    "driver_withdrawals",
    "driver_escrow",
    "driver_promo",
    "driver_deductions",
    "driver_additions",
    "driver_close_balance",
    "customer_open_balance",
    "customer_to_up",
    "customer_billed_charges",
    "customer_withdrawals",
    "customer_escrow",
    "customer_promo",
    "customer_deduction",
    "customer_additions",
    "customer_close_balance",
    "company_open_balance",
    "company_income_from_charges",
    "company_promotions",
    "company_withdraws_out",
    "company_close_balance",
  ];

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  };

  const fetchData = async () => {
    try {
      setRefreshing(true);
      const response = await fetch(
        `${API_URL}/application_statistics/get_last_statistic`
      );
      if (!response.ok) throw new Error("Failed to fetch data");
      const data = await response.json();
      setStatistics(data[0]);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          `${API_URL}/application_statistics/get_app_stats_by_dates/2024-12-01/2025-03-14`
        );
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const result = await response.json();
        setData(result); // Assuming the result is in the correct format
      } catch (err) {
        setError(err.message); // Set error message
      } finally {
        setLoading(false); // Set loading to false
      }
    };

    fetchData();
  }, []); // Empty dependency array means this effect runs once on mount

  const filteredStatistics = useMemo(() => {
    if (!statistics) return [];
    return Object.entries(statistics).filter(
      ([key]) => !keysToIgnore.includes(key)
    );
  }, [statistics]);

  const currentEntries = filteredStatistics.slice(
    currentPage * ITEMS_PER_PAGE,
    (currentPage + 1) * ITEMS_PER_PAGE
  );

  const totalPages = Math.ceil(filteredStatistics.length / ITEMS_PER_PAGE);

  const formatLabel = (key) => {
    return key
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const formatValue = (key, value) => {
    if (typeof value === "number") {
      return value.toLocaleString();
    }
    return value;
  };

  const aggregatedData = useMemo(() => {
    if (!data || data.length === 0) return {}; // Handle empty data

    const latest = data[data.length - 1];

    return {
      users: {
        customers: latest.customers_count || 0,
        drivers: latest.drivers_count || 0,
        newCustomers: latest.new_customer_count || 0,
        newDrivers: latest.new_drivers_count || 0,
      },
      trips: {
        count: latest.trips_count || 0,
        averageRate: latest.average_trip_rate_usd || 0,
        kmsBilled: latest.kms_billed || 0,
        perCustomer: latest.trips_per_customer || 0,
      },
      financial: {
        companyIncome: parseFloat(latest.company_income_from_charges) || 0,
        companyPromotions: parseFloat(latest.company_promotions) || 0,
        companyBalance: latest.company_close_balance || 0,
        totalCommission:
          (latest.sum_commission_uw || 0) + (latest.sum_commission_vw || 0),
      },
      ratings: {
        average: latest.ave_rating_count || 0,
        total: 10,
        complaints: parseInt(latest.complaints_count) || 0,
      },
    };
  }, [data]);

  const StatCard = ({ icon: Icon, title, value, color }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`p-6 rounded-xl shadow-lg ${
        isDarkMode ? "bg-gray-800" : "bg-white"
      }`}
    >
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-full ${color}`}>
          <Icon
            className={`w-6 h-6 ${isDarkMode ? "text-white" : "text-gray-800"}`}
          />
        </div>
        <div>
          <h3
            className={`text-sm font-medium ${
              isDarkMode ? "text-gray-400" : "text-gray-500"
            }`}
          >
            {title}
          </h3>
          <p
            className={`text-2xl font-bold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {value}
          </p>
        </div>
      </div>
    </motion.div>
  );

  if (loading) {
    return <div>Loading...</div>; // Loading state
  }

  if (error) {
    return <div>Error: {error}</div>; // Error state
  }

  return (
    <div
      className={`min-h-screen p-6 ${
        isDarkMode ? "bg-gray-900" : "bg-gray-50"
      }`}
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1
            className={`text-3xl font-bold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Application Statistics Dashboard
          </h1>
          <button
            onClick={() => setIsDarkMode(!isDarkMode)}
            className={`px-4 py-2 rounded-lg ${
              isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900"
            } shadow-lg`}
          >
            {isDarkMode ? "🌞 Light Mode" : "🌙 Dark Mode"}
          </button>
        </div>

        <div className="relative">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence mode="wait">
              {currentEntries.map(([key, value], index) => (
                <motion.div
                  key={key}
                  variants={cardVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className={`
                                        p-6 rounded-xl backdrop-blur-sm
                                        ${
                                          isDarkMode
                                            ? "bg-gray-800/90 text-white"
                                            : "bg-white/90 text-gray-800"
                                        }
                                        shadow-lg hover:shadow-xl transition-all duration-300
                                        transform hover:scale-105
                                    `}
                >
                  <h3 className="text-lg font-medium mb-2 opacity-80">
                    {formatLabel(key)}
                  </h3>
                  <p className="text-2xl font-bold">
                    {formatValue(key, value)}
                  </p>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          <div className="flex justify-center mt-6 gap-4 p-4">
            <button
              onClick={() => handlePageChange("prev")}
              className={`p-2 rounded-full ${
                isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-800"
              } shadow-lg`}
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={() => handlePageChange("next")}
              className={`p-2 rounded-full ${
                isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-800"
              } shadow-lg`}
            >
              <ChevronRight size={24} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-9">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`p-6 rounded-xl shadow-lg ${
              isDarkMode ? "bg-gray-800" : "bg-white"
            }`}
          >
            <h2
              className={`text-xl font-bold mb-4 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              User Growth
            </h2>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart
                data={data}
                margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="datefor"
                  angle={-45}
                  textAnchor="end"
                  interval={0} // Show all ticks
                  tick={{ fontSize: 12 }} // Adjust font size for clarity
                />
                <YAxis />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="customers_count"
                  fill="#60A5FA"
                  stroke="#2563EB"
                />
                <Area
                  type="monotone"
                  dataKey="drivers_count"
                  fill="#34D399"
                  stroke="#059669"
                />
              </AreaChart>
            </ResponsiveContainer>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`p-6 rounded-xl shadow-lg ${
              isDarkMode ? "bg-gray-800" : "bg-white"
            }`}
          >
            <h2
              className={`text-xl font-bold mb-4 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Financial Overview
            </h2>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={[aggregatedData.financial]}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis />
                <YAxis />
                <Tooltip />
                <Bar dataKey="companyIncome" fill="#60A5FA" />
                <Bar dataKey="companyPromotions" fill="#34D399" />
                <Bar dataKey="totalCommission" fill="#F472B6" />
              </BarChart>
            </ResponsiveContainer>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`p-6 rounded-xl shadow-lg col-span-2 ${
              isDarkMode ? "bg-gray-800" : "bg-white"
            }`}
          >
            <h2
              className={`text-xl font-bold mb-4 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Trip Statistics
            </h2>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="datefor" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="trips_count" stroke="#60A5FA" />
                <Line type="monotone" dataKey="kms_billed" stroke="#34D399" />
              </LineChart>
            </ResponsiveContainer>
          </motion.div>

          {/* <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`p-6 rounded-xl shadow-lg ${
              isDarkMode ? "bg-gray-800" : "bg-white"
            }`}
          >
            <h2
              className={`text-xl font-bold mb-4 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Rating Distribution
            </h2>
           
          </motion.div> */}
        </div>
      </div>
    </div>
  );
};

export default StsDashboard;
